import React, { useEffect, useState } from 'react';

// components
import Button from 'components/UiKit/Button';
import Accordion from 'components/UiKit/Accordion';
import TextField from 'components/UiKit/FieldText';
import FieldPhone from 'components/UiKit/FieldPhone';
import FieldSelect from 'components/UiKit/FieldSelect';
import ShippingForm from 'pages/Checkout/components/ShippingForm';
import ItemAddressShipping from 'pages/Checkout/components/ItemAddress';

// Constants
import { codesFlag } from 'constans/code-flag';
import { configAddress, styleFieldSelect } from 'constans/checkout';

// Lib
import * as yup from 'yup';
import { shallowEqual } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { convertDataAddresses, patternRxpCheckEng } from 'lib/helpers';
import { useAppDispatch, useAppSelector } from 'lib/hooks/useDispatch';

// Actions
import * as actionsCreator from 'pages/Checkout/reducer';
import { handlerShowForm, updateAddresses } from 'pages/Checkout/reducer';

// Type
type PropsAddresses = {
	[index: string]: string | number | boolean;
}

// Schema
const schemaBilling = yup.object({
	// Billing Addresses
	zip: yup.string().required('Zip is required'),
	country: yup.string().required('Country field is required'),
	state: yup.string().when('country', {
		is: (country: string) => country === 'US',
		then: yup.string().required('State is required for USA'),
		otherwise: yup.string(),
	}),
	city: yup.string().matches(patternRxpCheckEng, 'Use Latin symbols only.').required('City is required'),
	streetAddress: yup.string().matches(patternRxpCheckEng, 'Use Latin symbols only.').required('Street Addresses is required'),
	phone: yup.string().min(5, 'Please type numbers only').matches(/^[^X]*$/, 'Please type numbers only').required('Phone is required')
}).required();
const schemaShipping = yup.object({
	// ShipAddress
	cityShipAddress: yup.string().matches(patternRxpCheckEng, 'Use Latin symbols only.').when('shipAddress', {
		is: false,
		then: yup.string().required('City is required'),
		otherwise: yup.string()
	}),
	countryShipAddress: yup.string().when('shipAddress', {
		is: false,
		then: yup.string().required('Country field is required'),
		otherwise: yup.string()
	}),
	stateShipAddress: yup.string().when('countryShipAddress', {
		is: (country: string) => country === 'US',
		then: yup.string().required('State is required for USA'),
		otherwise: yup.string(),
	}),
	streetShipAddress: yup.string().matches(patternRxpCheckEng, 'Use Latin symbols only.').when('streetAddress', {
		is: false,
		then: yup.string().required('Street field is required'),
		otherwise: yup.string()
	}),
	zipShipAddress: yup.string().when('zip', {
		is: false,
		then: yup.string().required('Zipcode is a required field'),
		otherwise: yup.string()
	}),
	phoneShipAddress: yup.string().when('phone', {
		is: false,
		then: yup.string().required('Phone field is required'),
		otherwise: yup.string().min(5, 'Please type numbers only').matches(/^[^X]*$/, 'Please type numbers only').required('Phone is required')
	})
});
type FormDataBilling = yup.InferType<typeof schemaBilling>;
type FormDataShipping = yup.InferType<typeof schemaShipping>;

export const Address: React.FC = () => {
	const dispatch = useAppDispatch();

	const {
		countries,
		usaStates,

		loading,
		typeForm,
		listAddresses,
		loadingBilling,
		isShowFormShipping,
		currentShippingAddress
	} = useAppSelector((store) => {
		return {
			typeForm: store.checkout.typeForm,
			listAddresses: store.checkout.listAddresses,
			loading: store.checkout.loadingSendAddressShipping,
			loadingBilling: store.checkout.loadingUpdateAddresses,
			isShowFormShipping: store.checkout.isShowFormShipping,
			currentShippingAddress: store.checkout.currentShippingAddress,

			countries: store.settings.countries,
			usaStates: store.settings.usaStates,
		};
	}, shallowEqual);

	const [disabledFormBilling, setDisabledFormBilling] = useState<boolean>(true);
	const [isValidPhoneBilling, setIsValidPhoneBilling] = useState<boolean>(false);
	const [isValidPhoneShipping, setIsValidPhoneShipping] = useState<boolean>(false);

	const defaultValuesShipping: { [index: string]: any } = {
		countryShipAddress: currentShippingAddress?.country || '',
		cityShipAddress: currentShippingAddress?.city || '',
		stateShipAddress: currentShippingAddress?.state || '',
		phoneShipAddress: currentShippingAddress?.phone || '',
		zipShipAddress: currentShippingAddress?.postalCode || '',
		streetShipAddress: currentShippingAddress?.addressLine1 || ''
	};

	const {
		watch,
		control: controlBilling,
		setValue: setValueBilling,
		register: registerBillingField,
		formState: { errors: errorsBilling },
		handleSubmit: handleBillingFormSubmit,
		reset: resetBilling
	} = useForm<FormDataBilling>({
		resolver: yupResolver(schemaBilling)
		// defaultValues: defaultValuesBilling
	});
	const {
		reset: resetShipping,

		watch: watchShipping,
		control: controlShipping,
		setValue: setValueShipping,
		register: registerShippingField,
		formState: { errors: errorsShipping },
		handleSubmit: handleShippingFormSubmit
	} = useForm<FormDataShipping>({
		resolver: yupResolver(schemaShipping),
		defaultValues: defaultValuesShipping
	});

	const wCountry = watch('country');
	const wCountryShip = watchShipping('countryShipAddress');
	const isUSA = wCountry === 'US';
	const isUSAShipAddress = wCountryShip === 'US'

	useEffect(() => {
		if (isShowFormShipping) {
			// Сбросить форму при каждом открытии
			resetShipping({
				countryShipAddress: '',
				cityShipAddress: '',
				stateShipAddress: '',
				phoneShipAddress: '',
				zipShipAddress: '',
				streetShipAddress: ''
			});
			setIsValidPhoneShipping(false); // сбрасываем валидацию номера
		}
	}, [isShowFormShipping]);;

	useEffect(() => {
		if (listAddresses?.length) {
			const findBillingAddress = listAddresses?.find(
				(x: PropsAddresses) => x.type === configAddress.billing
			) || {};

			const defaultValuesBilling: PropsAddresses = {
				city: findBillingAddress?.city || '',
				phone: findBillingAddress?.phone || '',
				zip: findBillingAddress?.postalCode || '',
				state: findBillingAddress?.state?.name || '',
				country: findBillingAddress?.country?.name || '',
				streetAddress: findBillingAddress?.addressLine1 || ''
			};
			resetBilling(defaultValuesBilling);
		}
	}, [listAddresses]);

	useEffect(() => {
		if ((!!currentShippingAddress && isShowFormShipping) || (typeForm === 'update')) {
			setValueShipping('countryShipAddress', currentShippingAddress?.country || "");
			setValueShipping('stateShipAddress', currentShippingAddress?.state || "");
			setValueShipping('cityShipAddress', currentShippingAddress?.city || '');
			setValueShipping('phoneShipAddress', currentShippingAddress?.phone || '');
			setValueShipping('zipShipAddress', currentShippingAddress?.postalCode || '');
			setValueShipping('streetShipAddress', currentShippingAddress?.addressLine1 || '');
		}

		if (!isShowFormShipping || (typeForm === 'create')) {
			setValueShipping('zipShipAddress', '');
			setValueShipping('cityShipAddress', '');
			setValueShipping('phoneShipAddress', '');
			setValueShipping('streetShipAddress', '');
			setValueShipping('countryShipAddress', '');
		}
	}, [
		typeForm,
		isShowFormShipping,
		currentShippingAddress
	]);

	const handlerChangeDisabledBilling = () => {
		setDisabledFormBilling(!disabledFormBilling);
	};

	const onSubmitBilling = (data: FormDataBilling) => {
		if (isValidPhoneBilling) {
			if (disabledFormBilling) return;

			const billingAddress: PropsAddresses = listAddresses?.find(
				(item: PropsAddresses) => item?.type === configAddress?.billing
			) || {};

			dispatch(actionsCreator.updateAddresses({
				type: 'update-form',
				updateFormData: data,
				id: billingAddress?.id,
				typeAddress: configAddress?.billing
			}));
		}
	};

	const onSubmitShipping = (data: FormDataShipping) => {
		if (isValidPhoneShipping) {
			if (typeForm === 'create') {
				dispatch(actionsCreator.sendAddressShipping(data));
			}

			if (typeForm === 'update') {
				const id = currentShippingAddress?.id ? +currentShippingAddress?.id : 0;
				dispatch(updateAddresses({
					id: id,
					type: 'update-form',
					updateFormData: data,
					typeAddress: configAddress?.shipping
				}));
			}

			const timeout = setTimeout(() => {
				dispatch(handlerShowForm(false));
			}, 300);

			clearTimeout(timeout);
		}
	};

	const toggleForm = () => {
		dispatch(actionsCreator.handlerShowForm(true));
		dispatch(actionsCreator.handlerChangeTypeForm('create'));
	};

	const filteredDataByShipping = convertDataAddresses(listAddresses, configAddress?.shipping);

	const label = typeForm === 'create'
		? 'Create'
		: 'Update';

	const options = countries?.map(
		(x: any) => ({ label: x?.fullName || '', value: x?.name || '' })
	);
	const optionsState = usaStates?.map(
		(x: any) => ({ label: x?.state || '', value: x?.name || '' })
	);

	const children = isUSAShipAddress && <div className="flex flex-ai-center mb20">
		<Controller
			name="stateShipAddress"
			control={controlShipping}
			rules={{ required: true }}
			render={({ field: { onChange, value } }) => {
				const findObject: { [index: string]: string } | undefined = optionsState?.find(
					(option: { [index: string]: string }) => option?.label === value
				);
				return (
					<FieldSelect
						required
						label="State *"
						value={findObject}
						options={optionsState}
						className="full-width"
						name="stateShipAddress"
						title="field is required"
						styles={styleFieldSelect}
						placeholder="Select state"
						isError={!!errorsShipping?.stateShipAddress}
						onChange={(val: any) => onChange(val.label)}
						msgError={errorsShipping?.stateShipAddress ? errorsShipping?.stateShipAddress.message : ''}
						register={registerShippingField('stateShipAddress', { required: true })}
					/>
				);
			}}
		/>
	</div>

	return (
		<>
			<div className="account__address mb10">
				<Accordion
					isOpen
					title="Billing address"
					classNameTitle="mb20 pl10"
					className="account__address-border"
					headerEnd={
						<Button
							isSvg
							icon="editSvg"
							color="error"
							variable="icon"
							className="mr10"
							title="Edit the address"
							onPress={handlerChangeDisabledBilling}
						/>
					}
				>
					<form onSubmit={handleBillingFormSubmit(onSubmitBilling)}>
						<div className="flex flex-ai-center flex-direction-column-desktop mb20">
							<Controller
								name="country"
								control={controlBilling}
								rules={{ required: true }}
								render={({ field: { onChange, value } }) => {
									const findObject: { [index: string]: string } | undefined = options?.find(
										(option: { [index: string]: string }) => option?.value === value
									);
									return (
										<FieldSelect
											required
											name="country"
											label="Country *"
											options={options}
											value={findObject}
											className="account__col"
											title="field is required"
											styles={styleFieldSelect}
											placeholder="Select country"
											disabled={disabledFormBilling}
											isError={!!errorsBilling?.country}
											onChange={(val: any) => onChange(val.value)}
											msgError={errorsBilling?.country ? errorsBilling?.country.message : ''}
											register={registerBillingField('country', { required: true })}
										/>
									);
								}}
							/>
							<TextField
								required
								name="city"
								label="City/Town *"
								className="account__col"
								style={{ width: '100%' }}
								placeholder="Your answer"
								title="field is required"
								disabled={disabledFormBilling}
								isError={!!errorsBilling?.city}
								msgError={errorsBilling?.city ? errorsBilling?.city.message : ''}
								register={registerBillingField('city', { required: true })}
							/>
						</div>

						{isUSA && <div className="flex flex-ai-center mb20">
							<Controller
								name="state"
								control={controlBilling}
								rules={{ required: true }}
								render={({ field: { onChange, value } }) => {
									const findObject: { [index: string]: string } | undefined = optionsState?.find(
										(option: { [index: string]: string }) => option?.value === value
									);
									return (
										<FieldSelect
											required
											name="state"
											label="State *"
											value={findObject}
											options={optionsState}
											className="full-width"
											title="field is required"
											styles={styleFieldSelect}
											placeholder="Select state"
											disabled={disabledFormBilling}
											isError={!!errorsBilling?.state}
											onChange={(val: any) => onChange(val.value)}
											msgError={errorsBilling?.state ? errorsBilling?.state.message : ''}
											register={registerBillingField('state', { required: true })}
										/>
									);
								}}
							/>
						</div>}

						<div className="flex flex-ai-center flex-direction-column-desktop mb20">
							<TextField
								required
								name="zip"
								placeholder="0000"
								label="Postcode/ZIP *"
								className="account__col"
								style={{ width: '100%' }}
								title="field is required"
								isError={!!errorsBilling?.zip}
								disabled={disabledFormBilling}
								msgError={errorsBilling?.zip ? errorsBilling?.zip.message : ''}
								register={registerBillingField('zip', { required: true })}
							/>
							<Controller
								name="phone"
								control={controlBilling}
								rules={{ required: true }}
								render={({ field: { onChange, value } }) => (
									<FieldPhone
										big
										required
										name="phone"
										maskChar="X"
										value={value}
										countryCode="UA"
										options={codesFlag}
										placeholderChar="X"
										label="Phone number *"
										className="account__col"
										style={{ width: '100%' }}
										title="field is required"
										disabled={disabledFormBilling}
										setError={setIsValidPhoneBilling}
										error={!!errorsBilling?.phone?.message}
										onChange={(name: string, value: string) => onChange(value)}
										msgError={errorsBilling?.phone ? errorsBilling?.phone.message : ''}
										register={registerBillingField('phone', { required: true, pattern: /^\S+@\S+$/i })}
									/>
								)}
							/>
						</div>

						<TextField
							required
							name="streetAddress"
							label="Street address *"
							style={{ width: '100%' }}
							title="field is required"
							className="full-width mb20"
							disabled={disabledFormBilling}
							isError={!!errorsBilling?.streetAddress}
							placeholder="House number and street name"
							register={registerBillingField('streetAddress', { required: true })}
							msgError={errorsBilling?.streetAddress ? errorsBilling?.streetAddress.message : ''}
						/>

						<Button
							type="submit"
							label="Update"
							variable="info"
							className="right-button"
							loading={loadingBilling}
							disabled={disabledFormBilling}
						/>
					</form>
				</Accordion>

				<Accordion
					title="Shipping addresses"
					classNameTitle="mb20 pl10"
					className="molecule-product__border-accordion"
					headerEnd={
						<Button
							variable="href"
							color="green"
							onPress={toggleForm}
							label="+Add another address"
							className="account__add-address-btn"
						/>
					}
				>
					<>
						{filteredDataByShipping?.length
							? filteredDataByShipping?.map(
								(item: any, i: number) => <ItemAddressShipping
									item={item}
									type={configAddress.shipping}
									key={`${item.firstName}_${i}`}
								/>
							)
							: <p className="account__address-shipping-empty pl10">
								You don't have any shipping addresses
							</p>
						}
					</>
				</Accordion>
			</div>

			{isShowFormShipping && <form
				className="account__address-shipping"
				onSubmit={handleShippingFormSubmit(onSubmitShipping)}
			>
				<ShippingForm
					children={children}
					errors={errorsShipping}
					control={controlShipping}
					register={registerShippingField}
					setError={setIsValidPhoneShipping}
				/>

				<div className="flex flex-jc-end">
					<Button
						label="Hide"
						className="mr10"
						variable="outline"
						onPress={() => dispatch(handlerShowForm(false))}
					/>
					<Button
						type="submit"
						label={label}
						variable="green"
						loading={loading}
					/>
				</div>
			</form>}
		</>
	);
};

export default Address;
